/**
 * The forms catalog, and the way to what you have been asked to sign.
 */

import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { hasAtLeast } from '../../types/roles';
import { FILTER_MAX } from '../../types/fieldLimits';
import { useAddons } from '../org/useAddons';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import { SegmentedChoice } from '../../shared/SegmentedChoice';
import { isCallFailure } from '../../lib/callable';
import { formatDay } from '../../shared/dates';
import { useForms, useMyFormAssignments } from './useForms';
import { FORM_STATUSES, STATUS_LABEL, STATUS_TONE, type FormStatus } from './types';
import styles from '../../shared/Page.module.css';
import form from '../../shared/Form.module.css';
import own from './Forms.module.css';

export const LOCKED_TITLE = 'Forms and attestations is not turned on';

export function FormListPage() {
  const { session } = useAuth();
  const orgId = session?.orgId ?? null;
  const uid = session?.uid ?? null;
  const roles = session?.roles ?? [];
  const author = hasAtLeast(roles, 'author');

  const addons = useAddons(orgId);
  const enabled = addons.data?.formsAttestation === true;
  const list = useForms(orgId, enabled);
  const mine = useMyFormAssignments(orgId, uid, enabled);

  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const sp = params.get('status');
  const status: FormStatus | 'all' = (FORM_STATUSES as readonly string[]).includes(sp ?? '') ? (sp as FormStatus) : 'all';
  const setFilter = (next: { q?: string; status?: FormStatus | 'all' }) => {
    const p = new URLSearchParams();
    const nq = next.q ?? q; const ns = next.status ?? status;
    if (nq.trim()) p.set('q', nq);
    if (ns !== 'all') p.set('status', ns);
    setParams(p, { replace: true });
  };

  const rows = list.data ?? [];
  const shown = useMemo(() => {
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return rows.filter((f) => (status === 'all' || f.status === status) && words.every((w) => f.title.toLowerCase().includes(w)));
  }, [rows, q, status]);
  const filtered = q.trim() !== '' || status !== 'all';
  const due = (mine.data ?? []).filter((a) => a.status === 'pending' || a.status === 'overdue');
  const overdue = due.filter((a) => a.status === 'overdue').length;

  if (addons.isLoading) return <Spinner label="Loading" />;
  if (!enabled) {
    return (
      <>
        <header className={styles.header}><h1 className={styles.title}>Forms and attestations</h1></header>
        <div className={styles.body}><PageState title={LOCKED_TITLE}>This add-on is not enabled for your organization. Your StandingDocs administrator can turn it on.</PageState></div>
      </>
    );
  }

  return (
    <>
      <header className={styles.header}>
        <div className={styles.headerRow}>
          <div>
            <h1 className={styles.title}>Forms and attestations</h1>
            <p className={styles.subtitle}>
              Declarations and agreements staff sign: conflicts of interest, confidentiality, the code of conduct.
              Publishing a form assigns it with a due date; each signed copy is kept exactly as it was answered.
            </p>
          </div>
          {author && <div className={styles.actions}><Link to="/forms/new" className={`${styles.btn} ${styles.btnPrimary}`}>New form</Link></div>}
        </div>
      </header>

      <div className={styles.body}>
        <div className={own.mine} role="status">
          <p>
            {mine.isLoading ? 'Checking what is waiting for your signature…'
              : due.length === 0 ? 'Nothing is waiting for your signature.'
              : <><b className="tnum">{due.length}</b> {due.length === 1 ? 'form is' : 'forms are'} waiting for you{overdue > 0 && <>, <b className="tnum">{overdue}</b> overdue</>}.</>}
          </p>
          <Link to="/forms/mine" className={styles.btn}>Your forms</Link>
        </div>

        {list.error && <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">Could not load forms. {isCallFailure(list.error) ? list.error.message : ''}</div>}

        {list.isLoading ? <Spinner label="Loading forms" /> : rows.length === 0 ? (
          <div className={`${styles.notice} ${styles.noticeWarn}`} role="status">No forms yet. {author ? 'Build the first with New form.' : 'An author builds them.'}</div>
        ) : (
          <>
            <div className={own.filters}>
              <div className={own.filterText}>
                <label className={form.label} htmlFor="form-filter">Filter</label>
                <input id="form-filter" className={form.input} type="text" maxLength={FILTER_MAX} autoComplete="off" value={q} onChange={(e) => setFilter({ q: e.target.value })} placeholder="Title" />
              </div>
              <SegmentedChoice legend="Status" name="form-status" value={status} onChange={(v) => setFilter({ status: v })}
                options={[{ value: 'all' as const, label: `All (${rows.length})` }, ...FORM_STATUSES.map((s) => ({ value: s, label: `${STATUS_LABEL[s]} (${rows.filter((r) => r.status === s).length})` }))]} />
            </div>
            <p className={own.count} role="status" aria-live="polite">
              <span><b className="tnum">{shown.length}</b> of <span className="tnum">{rows.length}</span></span>
              {filtered && <button type="button" className={own.linkBtn} onClick={() => setParams(new URLSearchParams(), { replace: true })}>Clear filters</button>}
            </p>
            <div className={own.tableWrap}>
              <table className={own.table}>
                <thead><tr><th scope="col">Form</th><th scope="col">Version</th><th scope="col">Status</th><th scope="col">Updated</th></tr></thead>
                <tbody>
                  {shown.map((f) => (
                    <tr key={f.id}>
                      <td className={own.titleCell}><Link to={`/forms/${f.id}`}>{f.title}</Link></td>
                      <td className={`${own.num} ${f.currentVersionNumber ? '' : own.muted}`}>{f.currentVersionNumber ? `v${f.currentVersionNumber}` : '—'}</td>
                      <td><span className={`${styles.tag} ${styles[STATUS_TONE[f.status]]}`}>{STATUS_LABEL[f.status]}</span></td>
                      <td className={`${own.num} ${f.updatedAt ? '' : own.muted}`}>{formatDay(f.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {shown.length === 0 && <p className={styles.subtitle}>Nothing matches. <strong>Clear filters</strong> to see all {rows.length}.</p>}
            </div>
          </>
        )}
      </div>
    </>
  );
}
